import React, { useState } from 'react';
import { Sidebar } from '../../components/Sidebar';
import { Header } from '../../components/Header';
import { CheckCircleIcon, ImageIcon, DownloadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const FORMATS = [{
  id: '1:1',
  name: 'Square (1:1)',
  desc: 'Facebook Feed, Instagram Post'
}, {
  id: '9:16',
  name: 'Portrait (9:16)',
  desc: 'Instagram Story, YouTube Shorts'
}, {
  id: '16:9',
  name: 'Landscape (16:9)',
  desc: 'Google Display, YouTube'
}, {
  id: '4:5',
  name: 'Portrait (4:5)',
  desc: 'Facebook/Instagram Feed'
}];
export function ResizerFormats() {
  const navigate = useNavigate();
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeProgress, setResizeProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const toggleFormat = (formatId: string) => {
    setSelectedFormats(prev => prev.includes(formatId) ? prev.filter(id => id !== formatId) : [...prev, formatId]);
  };
  const handleResize = () => {
    setIsResizing(true);
    const interval = setInterval(() => {
      setResizeProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsResizing(false);
          setIsComplete(true);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-1393">
      <Sidebar data-id="element-1394" />
      <main className="flex-1" data-id="element-1395">
        <Header data-id="element-1396" />
        <div className="px-6 py-8" data-id="element-1397">
          <div className="max-w-5xl mx-auto" data-id="element-1398">
            <div className="mb-6" data-id="element-1399">
              <button onClick={() => navigate('/dealer/resizer/step-1')} className="text-sm text-blue-500 hover:text-blue-600 mb-2 transition-colors" data-id="element-1400">
                ← Back to Upload
              </button>
              <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-1401">
                Select Target Formats
              </h1>
              <p className="text-gray-600" data-id="element-1402">
                Choose which formats you want to generate
              </p>
            </div>

            {!isComplete ? <div className="space-y-6" data-id="element-1403">
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1404">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1405">
                    Available Formats
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1406">
                    {FORMATS.map(format => <button key={format.id} onClick={() => toggleFormat(format.id)} className={`p-4 rounded-lg border-2 transition-all text-left ${selectedFormats.includes(format.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1407">
                        <div className="flex items-center justify-between mb-2" data-id="element-1408">
                          <span className="font-medium text-gray-800" data-id="element-1409">
                            {format.name}
                          </span>
                          {selectedFormats.includes(format.id) && <CheckCircleIcon className="text-blue-500 flex-shrink-0" size={20} data-id="element-1410" />}
                        </div>
                        <p className="text-sm text-gray-500" data-id="element-1411">{format.desc}</p>
                      </button>)}
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1412">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1413">
                    Preview Comparison
                  </h3>
                  <div className="grid grid-cols-2 gap-6" data-id="element-1414">
                    <div data-id="element-1415">
                      <p className="text-sm text-gray-600 mb-2" data-id="element-1416">
                        Original (16:9)
                      </p>
                      <div className="w-full h-48 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center" data-id="element-1417">
                        <ImageIcon className="text-gray-400" size={48} data-id="element-1418" />
                      </div>
                    </div>
                    <div data-id="element-1419">
                      <p className="text-sm text-gray-600 mb-2" data-id="element-1420">
                        {selectedFormats.length > 0 ? `Target (${selectedFormats[0]})` : 'Select a format'}
                      </p>
                      <div className="w-full h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center" data-id="element-1421">
                        <ImageIcon className="text-blue-400" size={48} data-id="element-1422" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1423">
                  <h4 className="font-medium text-blue-900 mb-2" data-id="element-1424">
                    AI Resizing Features
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-1" data-id="element-1425">
                    <li data-id="element-1426">
                      • <strong data-id="element-1427">Smart Crop:</strong> AI identifies and preserves
                      focal points
                    </li>
                    <li data-id="element-1428">
                      • <strong data-id="element-1429">Intelligent Expansion:</strong> Adds contextual
                      background to fill space
                    </li>
                    <li data-id="element-1430">
                      • <strong data-id="element-1431">Quality Preservation:</strong> Maintains
                      original image quality
                    </li>
                  </ul>
                </div>

                {isResizing && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1432">
                    <h3 className="font-medium text-gray-800 mb-4" data-id="element-1433">
                      Resizing Creatives...
                    </h3>
                    <div className="mb-4" data-id="element-1434">
                      <div className="flex justify-between text-sm text-gray-600 mb-2" data-id="element-1435">
                        <span data-id="element-1436">Progress</span>
                        <span data-id="element-1437">{resizeProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3" data-id="element-1438">
                        <div className="bg-blue-500 h-3 rounded-full transition-all duration-300" style={{
                    width: `${resizeProgress}%`
                  }} data-id="element-1439"></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1440">
                      AI is resizing your creative to {selectedFormats.length}{' '}
                      format{selectedFormats.length > 1 ? 's' : ''}...
                    </p>
                  </div>}

                <div className="flex justify-between" data-id="element-1441">
                  <button onClick={() => navigate('/dealer/resizer/step-1')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1442">
                    Back
                  </button>
                  <button onClick={handleResize} disabled={selectedFormats.length === 0 || isResizing} className="px-8 py-3 bg-blue-500 text-white rounded-md font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-1443">
                    {isResizing ? 'Resizing...' : 'Resize Creatives'}
                  </button>
                </div>
              </div> : <div className="space-y-6" data-id="element-1444">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-id="element-1445">
                  <div className="flex items-center" data-id="element-1446">
                    <CheckCircleIcon className="text-green-500 mr-3 flex-shrink-0" size={32} data-id="element-1447" />
                    <div data-id="element-1448">
                      <h2 className="text-xl font-semibold text-green-900" data-id="element-1449">
                        Resizing Complete!
                      </h2>
                      <p className="text-sm text-green-700" data-id="element-1450">
                        {selectedFormats.length} format variation
                        {selectedFormats.length > 1 ? 's' : ''} created
                        successfully
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-1451">
                  <h3 className="font-medium text-gray-800 mb-4" data-id="element-1452">
                    Download Resized Creatives
                  </h3>
                  <div className="grid grid-cols-4 gap-4" data-id="element-1453">
                    {selectedFormats.map(formatId => {
                  const format = FORMATS.find(f => f.id === formatId);
                  return <div key={formatId} className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center" data-id="element-1454">
                          <div className="w-full h-24 bg-gradient-to-br from-blue-100 to-blue-200 rounded mb-2 flex items-center justify-center" data-id="element-1455">
                            <ImageIcon className="text-blue-400" size={32} data-id="element-1456" />
                          </div>
                          <p className="text-xs font-medium text-gray-700 mb-1" data-id="element-1457">
                            {format?.name}
                          </p>
                          <DownloadIcon className="mx-auto text-gray-500" size={16} data-id="element-1458" />
                        </div>;
                })}
                  </div>
                </div>

                <div className="flex justify-center space-x-4" data-id="element-1459">
                  <button className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium transition-colors" data-id="element-1460">
                    Download All Files
                  </button>
                  <button onClick={() => navigate('/creative-customizer')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1461">
                    Resize Another
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </main>
    </div>;
}